import React, { useRef, useEffect } from 'react';
import { Box, Edges } from '@react-three/drei';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import InfoPoint from './InfoPoint';

const SMALLSIZE = 0.001

function Pergola3D({ config, infosVisible, darkMode }) {
  const stripsRef = useRef([]);
  const animationStartTime = useRef(0);
  const hasModeChanged = useRef(false);
  const previousRotationMode = useRef(config.stripRotationHorizontal);

  // **CONFIG PARAMETERS**
  const modulesCount = config.modules || 1;
  const { width, length, height } = config;

  // **STRUCTURAL PARAMETERS**
  const columnSize = 0.13;
  const columnOffset = columnSize / 2;
  const frameHeight = 0.15;
  const frameThickness = 0.03;
  const gutterSize = 0.1;
  const availableWidth = config.width - columnSize * (modulesCount - 1); // Subtract extra columns in between
  const moduleWidth = availableWidth / modulesCount;
  const adjustedWidth = moduleWidth - frameThickness * 2;
  const adjustedLength = length - frameThickness;
  const adjustedHeight = height - frameHeight - gutterSize;
  
  const glassVerticalPosition = height / 2 - frameHeight/2 - gutterSize/2 +0.01;
  const maxGlassWidth = 1.1;
  const minGlassWidth = 0.6;
  const glassThickness = 0.01;
  // **STRIPS PARAMETERS**
  const stripWidth = 0.18;
  const stripThickness = 0.04;
  const stripSpacing = 0.182;
  const numStrips = Math.floor(adjustedWidth / stripSpacing);

  // **LED PARAMETERS**
  const perimeterLedHeight = height - frameHeight - gutterSize;
  const ledThickness = 0.01;
  const roofLedLength = config.stripRotationHorizontal? length - gutterSize * 2 - 0.3: moduleWidth- gutterSize * 2 - 0.3;
  const roofLedDistributionEvery = 3;

  const infoPoints = [
    {
      position: [0, height + 0.1, 0], // Roof center
      title: "Tetto Motorizzato",
      description:
        "Il tetto è dotato di un sistema motorizzato per regolare l'apertura e la chiusura delle lamelle.",
      images: ["/images/motorized-roof.jpg","/images/motorized-roof-2.jpg"],
      visible: config.motorized,
    },
    {
      position: [-width / 2 -0.2 + columnSize, height / 3, 0], // Back Glass Wall
      title: "Vetro di Chiusura",
      description:
        "Pareti in vetro temperato per una maggiore protezione e isolamento.",
      images: ["/images/glass-walls.jpg","/images/glass-walls-2.jpg"],
      visible: config.glass,
    },
    {
      position: [0, height -0.1 , -length / 2 - columnSize], // Left column
      title: "Fissaggio al Muro",
      description:
        "Il pergolato può essere montato a parete per una maggiore stabilità.",
      images: ["/images/wall-fix.jpg"],
      visible: config.wallMount,
    },
    {
      position: [0, perimeterLedHeight-0.1, length / 2 ], // Front LED strip
      title: "Illuminazione LED",
      description:
        "L'illuminazione a LED perimetrale garantisce un'illuminazione elegante e moderna.",
      images: ["/images/led-lighting.jpg"],
      visible: config.perimeterLed,
    },
  ];

  // **CREATE MODULES ARRAY**
  const modules = Array.from({ length: config.modules }, (_, i) => {
    const isFirst = i === 0;
    const isLast = i === config.modules - 1;
    const isMiddle = !isFirst && !isLast;
  
    // Calculate module width dynamically
    const moduleWidth = (config.width) / config.modules; // Adjusted module width
    const totalColumnsWidth = (config.modules - 1) * columnSize; // Width taken by shared columns
  
    // X position of the module
    const startX = -config.width / 2 + moduleWidth / 2;
    const positionX = startX + i * (moduleWidth);
  
    return { 
      width: moduleWidth,
      length: config.length,
      height: config.height,
      position: [positionX, config.height / 2, 0], 
      isFirst, 
      isMiddle, 
      isLast 
    };
  });
  

  // **STRIP ANIMATION**
  useEffect(() => {
    hasModeChanged.current = true;
    stripsRef.current = new Array(numStrips).fill(null);
  
    if (previousRotationMode.current !== config.stripRotationHorizontal) {
      stripsRef.current.forEach((strip) => {
        if (strip) {
          strip.rotation.set(0, 0, 0);
          strip.position.set(strip.userData.originalPosition);
        }
      });
  
      stripsRef.current = [];
      previousRotationMode.current = config.stripRotationHorizontal;
    }
  }, [config.stripRotationHorizontal]);
  
  useFrame(({ clock }) => {
    if (config.motorized && stripsRef.current.length > 0) {
      if (hasModeChanged.current) {
        animationStartTime.current = clock.getElapsedTime();
        hasModeChanged.current = false;
        // stripsRef.current.forEach((strip) => strip?.rotation.set(0, 0, 0));
      }
  
      const elapsedTime = clock.getElapsedTime() - animationStartTime.current;
      const angle = (Math.sin(elapsedTime / 5 * Math.PI * 2) + 1) * 45;
      const radians = (angle * Math.PI) / 180;
  
      Object.values(stripsRef.current).forEach((moduleStrips, moduleIndex) => {
        if (Array.isArray(moduleStrips)) {
          moduleStrips.forEach((strip, index) => {
            if (strip && index!==0) {
              const { stripWidth } = strip.userData.dimensions;
              if (config.stripRotationHorizontal) {
                strip.rotation.z = radians;
              } else {
                strip.rotation.x = radians;
              }
              strip.position.y = Math.sin(radians) * -(stripWidth / 2) + (height - frameThickness / 2);
            }
          });
        }
      });
    }
  });
  

  if (width <= 0 || length <= 0) return null;

  const renderColumns = () =>
    modules.map(({ position, width, isFirst, isMiddle, isLast }, i) => {
      const sharedRightColumnX = position[0] + width / 2; // Shared column between modules
      const hasNextModule = i < modules.length - 1;
      const columnHeight = height - frameHeight;
      const columnYPosition = height / 2 - frameHeight / 2;
  
      return (
        <React.Fragment key={`columns-module-${i}`}>
          {/* First Module: Has front & back left columns */}
          {isFirst && (
            <>
              {/* Front-Left Column */}
              <Box
                args={[columnSize, columnHeight, columnSize]}
                position={[position[0] - width / 2 + columnOffset, columnYPosition, length / 2 - columnOffset]}
              >
                <meshStandardMaterial color={config.color} />
              </Box>
  
              {/* Back-Left Column: Uses smaller column if wall-mounted */}
              {config.wallMount ? (
                <Box
                  args={[columnSize, frameHeight + gutterSize, columnSize]}
                  position={[position[0] - width / 2 + columnOffset, height - (frameHeight + gutterSize) / 2, -length / 2 + columnOffset]}
                >
                  <meshStandardMaterial color={config.color} />
                </Box>
              ) : (
                <Box
                  args={[columnSize, columnHeight, columnSize]}
                  position={[position[0] - width / 2 + columnOffset, columnYPosition, -length / 2 + columnOffset]}
                >
                  <meshStandardMaterial color={config.color} />
                </Box>
              )}
            </>
          )}
  
          {/* Front-Right Column (For all modules, but positioned differently for middle ones) */}
          <Box
            args={[columnSize, columnHeight, columnSize]}
            position={[
              hasNextModule 
              ? sharedRightColumnX 
              : position[0] + width / 2 - columnOffset,
              columnYPosition,
              length / 2 - columnOffset
            ]}
          >
            <meshStandardMaterial color={config.color} />
          </Box>
  
          {/* Back-Right Column (For all modules, but positioned differently for middle ones) */}
          {config.wallMount ? (
          <Box
            args={[columnSize, frameHeight + gutterSize, columnSize]}
            position={[hasNextModule ? sharedRightColumnX : position[0] + width / 2 - columnOffset, height - (frameHeight + gutterSize) / 2, -length / 2 + columnOffset]}
          >
            <meshStandardMaterial color={config.color} />
          </Box>
        ) : (
          <Box
            args={[columnSize, columnHeight, columnSize]}
            position={[
              hasNextModule ? sharedRightColumnX : position[0] + width / 2 - columnOffset,
              columnYPosition,
              -length / 2 + columnOffset,
            ]}
          >
            <meshStandardMaterial color={config.color} />
          </Box>
        )}
        </React.Fragment>
      );
    });
  
  // **RENDER FRAME FOR EACH MODULE**
  const renderFrame = () =>
    modules.map(({ width, position, isFirst, isLast }, i) => {
      const frameY = height - frameHeight / 2; // Y-position of the frame
      const frontZ = length / 2 - frameThickness / 2; // Front frame position
      const backZ = -length / 2 + frameThickness / 2; // Back frame position
      const leftX = position[0] - width / 2 + frameThickness / 2; // Left frame inside module
      const rightX = position[0] + width / 2 - frameThickness / 2; // Right frame inside module
      
      const hasNextModule = i < modules.length - 1;
      const moduleWidth = isFirst||isLast? width -columnSize-columnSize/2 -SMALLSIZE :width - columnSize -SMALLSIZE ; // Width of the module without columns
      const frameWidth = moduleWidth
      const frameLength = length - columnSize * 2 - SMALLSIZE
      
      return (
        <React.Fragment key={`frame-${i}`}>
          {/* 🔹 Front and Back Frames (Width of the module) */}
          <Box args={[frameWidth, frameHeight, frameThickness]} position={[isFirst?position[0] + columnSize/4: isLast?position[0] - columnSize/4:position[0], frameY, frontZ]}>
            <meshStandardMaterial color={config.color} />
          </Box>
          <Box args={[frameWidth, frameHeight, frameThickness]} position={[isFirst?position[0] + columnSize/4: isLast?position[0] - columnSize/4:position[0], frameY, backZ]}>
            <meshStandardMaterial color={config.color} />
          </Box>
  
          {/* 🔹 Left and Right Frames (Inside the module dimensions) */}
          <Box args={[frameThickness, frameHeight, frameLength]} position={[leftX, frameY, 0]}>
            <meshStandardMaterial color={config.color} />
          </Box>
          <Box args={[frameThickness, frameHeight, frameLength]} position={[rightX, frameY, 0]}>
            <meshStandardMaterial color={config.color} />
          </Box>
        </React.Fragment>
      );
    });
  
    const renderFrameContinuation = () =>
      modules.map(({ position, width, isFirst, isMiddle, isLast }, i) => {
        const frameY = height - frameHeight / 2; // Align with top of columns
        const cornerSize = columnSize; // Size of the corner connection elements
        const cornerSizeReduced = columnSize-frameThickness
        const frameThicknessExtended = frameThickness; // Thicker edge
        const hasNextModule = i < modules.length - 1;
    
        // Corner positions
        const leftX = position[0] - width / 2 + frameThickness / 2;
        const rightX = position[0] + width / 2 - frameThickness / 2;
        const frontZ = length / 2 - frameThickness / 2 ;
        const backZ = -length / 2 + frameThickness / 2;
        
        const sideFrontZ = frontZ - columnSize/2 + frameThickness/2
        const sideBackZ = backZ + columnSize/2 - frameThickness/2
    
        // **L Shape for Corners**
        const cornerElementSize = columnSize * 1.5; // Extend beyond the column slightly
    
        return (
          <React.Fragment key={`frame-continuation-${i}`}>
            {/* 🔹 L Shape on First and Last Modules */}
            {/* 🔹 Left Front Corner L */}
            {(
              <>
                <Box args={[cornerSizeReduced, frameHeight, frameThicknessExtended]} position={[isFirst?leftX+columnSize/2:leftX, frameY, frontZ ]}>
                  <meshStandardMaterial color={config.color} />
                </Box>
                <Box args={[frameThicknessExtended, frameHeight, cornerSize]} position={[leftX, frameY, sideFrontZ]}>
                  <meshStandardMaterial color={config.color} />
                </Box>
              </>
            )}
            {/* 🔹 Left Back Corner L */}
            {(
              <>
                <Box args={[cornerSizeReduced, frameHeight, frameThicknessExtended]} position={[isFirst?leftX+columnSize/2:leftX, frameY, backZ]}>
                  <meshStandardMaterial color={config.color} />
                </Box>
                <Box args={[frameThicknessExtended, frameHeight, cornerSize]} position={[leftX, frameY, sideBackZ]}>
                  <meshStandardMaterial color={config.color} />
                </Box>
              </>
            )}

            {/* 🔹 Right Front Corner L */}
            { (
              <>
                <Box args={[cornerSizeReduced, frameHeight, frameThicknessExtended]} position={[isLast?rightX-columnSize/2:rightX, frameY, frontZ]}>
                  <meshStandardMaterial color={config.color} />
                </Box>
                <Box args={[frameThicknessExtended, frameHeight, cornerSize]} position={[rightX, frameY, sideFrontZ]}>
                  <meshStandardMaterial color={config.color} />
                </Box>
              </>
            )}


            {/* 🔹 Right Back Corner L */}
            { (
              <>
                <Box args={[cornerSizeReduced, frameHeight, frameThicknessExtended]} position={[isLast?rightX-columnSize/2:rightX, frameY, backZ]}>
                  <meshStandardMaterial color={config.color} />
                </Box>
                <Box args={[frameThicknessExtended, frameHeight, cornerSize]} position={[rightX, frameY, sideBackZ]}>
                  <meshStandardMaterial color={config.color} />
                </Box>
              </>
            )}

          </React.Fragment>
        );
      });
    

  const renderGutters = () =>
    modules.map(({ position, width, isFirst, isMiddle, isLast }, i) => {
      const sharedRightGutterX = position[0] + width / 2; // Shared gutter between modules
      const leftGutterX = position[0] - width / 2 + gutterSize / 2;
      const rightGutterX = position[0] + width / 2 - gutterSize / 2;
      const gutterY = height - frameHeight - gutterSize / 2 -SMALLSIZE; // Adjusted height position
      const hasNextModule = i < modules.length - 1;
      const gutterWidth = isFirst||isLast? width -columnSize-columnSize/2 -SMALLSIZE:width - columnSize -SMALLSIZE; // Width of the module without columns

  
      return (
        <React.Fragment key={`gutters-module-${i}`}>
          {/* First Module: Has its own left gutter */}
          {isFirst && (
            <Box args={[gutterSize, gutterSize, length - columnSize * 2]} position={[leftGutterX, gutterY, 0]}>
              <meshStandardMaterial color={config.color} />
            </Box>
          )}
  
          {/* Right Gutter (Shared for middle modules, separate for first/last) */}
          <Box
            args={[gutterSize, gutterSize, length - columnSize * 2]}
            position={[
              hasNextModule ? sharedRightGutterX : rightGutterX,
              gutterY,
              0,
            ]}
          >
            <meshStandardMaterial color={config.color} />
          </Box>
  
          {/* Front and Back Gutters: Always drawn for each module */}
          <Box
            args={[gutterWidth, gutterSize, gutterSize]}
            position={[isFirst? position[0]+columnSize/4: isLast?position[0] - columnSize/4:position[0], gutterY, length / 2 - gutterSize / 2]}
          >
            <meshStandardMaterial color={config.color} />
          </Box>
          <Box
            args={[gutterWidth, gutterSize, gutterSize]}
            position={[isFirst? position[0]+columnSize/4: isLast?position[0] - columnSize/4:position[0], gutterY, -length / 2 + gutterSize / 2]}
          >
            <meshStandardMaterial color={config.color} />
          </Box>
        </React.Fragment>
      );
    });
    
  // **RENDER GLASS PANELS**
  const renderGlasses = () => {
    if (!config.glass) return null;
  
    return modules.map(({ width, position, isFirst, isLast }, i) => {
      const glassPanels = [];
  
      // Function to generate symmetrical glass panels **between columns**
      const generateGlassPanels = (wall, axis, pos, totalWidth, numColumns) => {
        const columnSpacing = columnSize * ( numColumns - 1 );
        const availableWidth = totalWidth - columnSpacing;
        let panelCount = Math.ceil(availableWidth / maxGlassWidth);
        let panelSize = availableWidth / panelCount;
  
        // Ensure the panel size is within the allowed range
        if (panelSize < minGlassWidth) {
          panelSize = minGlassWidth;
          panelCount = Math.ceil(availableWidth / panelSize);
        } else if (panelSize > maxGlassWidth) {
          panelSize = maxGlassWidth;
        }
        
  
        let totalPanelsWidth = panelCount * panelSize + (panelCount - 1) * 0.01; // Including sliding gaps
        let offset = -totalPanelsWidth / 2 + panelSize / 2;
        let glassSlidingOffset = 0;
  
        for (let index = 0; index < panelCount; index++) {
          glassPanels.push(
            <Box
              key={`${wall}-glass-${i}-${index}`}
              args={
                axis === "x"
                  ? [panelSize +0.02, adjustedHeight, glassThickness]
                  : [glassThickness, adjustedHeight, panelSize +0.02]
              }
              position={
                axis === "x"
                  ? [position[0] + offset, glassVerticalPosition, pos + glassSlidingOffset]
                  : [pos + glassSlidingOffset, glassVerticalPosition, offset]
              }
              
            >
              <meshStandardMaterial color="lightblue" transparent opacity={0.6} />
            </Box>
          );
  
          offset += panelSize + 0.01; // Add sliding gap
          glassSlidingOffset += 0.01;
        }
      };
  
      // **Front and Back Glass Panels (Between Columns)**
      if (config.glassWalls.retro) {
        generateGlassPanels("retro", "x", -length / 2 + columnSize/2, width  , 2);
      }
      if (config.glassWalls.fronte) {
        generateGlassPanels("fronte", "x", length / 2 - columnSize/2, width , 2);
      }
  
      // **Left & Right Depth Glass Panels (ONLY on first & last module)**
      if (isFirst && config.glassWalls.sinistra) {
        generateGlassPanels("sinistra", "z", position[0] - width / 2 + columnSize / 2, length - columnSize, 2);
      }
      if (isLast && config.glassWalls.destra) {
        generateGlassPanels("destra", "z", position[0] + width / 2 - columnSize / 2, length - columnSize, 2);
      }
  
      return <React.Fragment key={`glass-module-${i}`}>{glassPanels}</React.Fragment>;
    });
  };
  
  // **RENDER STRIPS**
  // const renderStrips = () => {
  //   return modules.map(({ width, length,  position, isFirst }, moduleIndex) => {
  //     const strips = [];
  
  //     const hasNextModule = moduleIndex < modules.length - 1;

  //     const adjustedModuleWidth =  width - frameThickness * 2;
  //     const adjustedModuleLength = length - frameThickness * 2;
  
  //     const numStrips = Math.floor(
  //       (config.stripRotationHorizontal ? adjustedModuleWidth : adjustedModuleLength) / stripSpacing
  //     );
  
  //     // Calculate total covered space and remaining space
  //     const totalCoveredSize = numStrips * stripSpacing;
  //     const remainingSpace = (config.stripRotationHorizontal ? adjustedModuleWidth  - totalCoveredSize +0.005 : adjustedModuleLength  - totalCoveredSize);
  
  //     // **Ensure stripsRef.current[moduleIndex] exists**
  //     if (!stripsRef.current[moduleIndex]) {
  //       stripsRef.current[moduleIndex] = [];
  //     }
  
  //     for (let i = 0; i < numStrips; i++) {
  //       // console.log(position[0]);
  //       // console.log(adjustedModuleWidth / 2 );
  //       // console.log((i * stripSpacing));
        
  //       const xPos = position[0] - (width / 2) + stripWidth + (i * stripSpacing) - columnSize/2;
  //       const zPos = -length / 2    + (i * stripSpacing )+ stripWidth/2+frameThickness
  //       const yPos = height - frameHeight + frameThickness
  
  //       // Define strip dimensions and positions based on orientation
  //       const stripArgs = config.stripRotationHorizontal
  //         ? [stripWidth, stripThickness, adjustedModuleLength] // Horizontal strips
  //         : [adjustedModuleWidth, stripThickness, stripWidth]; // Vertical strips
  
  //       const stripPosition = config.stripRotationHorizontal
  //         ? [xPos, yPos, position[2]] // Horizontal position
  //         : [position[0], yPos, zPos]; // Vertical strips
  
  //       strips.push(
  //         <Box
  //           key={`strip-${moduleIndex}-${i}`}
  //           args={stripArgs}
  //           position={stripPosition}
  //           ref={(el) => {
  //             if (el) stripsRef.current[moduleIndex][i] = el;
  //           }}
  //         >
  //           <meshStandardMaterial color={config.color} />
            
  //           {/* Roof LED (every nth strip) */}
  //           {config.roofLed && i % roofLedDistributionEvery === 0 && (
  //             <Box args={[0.01, ledThickness, roofLedLength]} position={[0, -stripThickness / 3, 0]}>
  //               <meshStandardMaterial color="white" emissive="white" emissiveIntensity={2} toneMapped={false} />
  //             </Box>
  //           )}
  //         </Box>
  //       );
  //     }
  
  //     // **Fixed strip for remaining space** (closing the aperture)
  //     if (remainingSpace > 0) {
  //       const yPos = height - frameHeight + frameThickness
  //       const xPos = config.stripRotationHorizontal
  //       ? position[0] + adjustedModuleWidth / 2 - remainingSpace / 2
  //       :position[0] ;
        
  //       const fixedStripArgs = config.stripRotationHorizontal
  //         ? [remainingSpace, stripThickness, adjustedModuleLength]
  //         : [adjustedModuleWidth, stripThickness, remainingSpace];
  
  //       const fixedStripPosition = config.stripRotationHorizontal
  //         ? [xPos, yPos, 0]
  //         : [xPos, yPos, adjustedModuleLength / 2 - remainingSpace / 2];
  
  //       strips.push(
  //         <Box key={`fixed-strip-${moduleIndex}`} args={fixedStripArgs} position={fixedStripPosition}>
  //           <meshStandardMaterial color={"gray"} />
  //         </Box>
  //       );
  //     }
  
  //     return <React.Fragment key={`strips-module-${moduleIndex}`}>{strips}</React.Fragment>;
  //   });
  // };

  const renderStrips = () => {
    return modules.map(({ width, length, position }, moduleIndex) => {
      const strips = [];
  
      const stripSideMargin = 0.02;
      const adjustedModuleWidth = width - frameThickness * 2 - stripSideMargin;
      const adjustedModuleLength = length - frameThickness * 2 - stripSideMargin;

      const effectiveStripSpacing = stripSpacing - 0.03;
  
      const numStrips = Math.floor(
        (config.stripRotationHorizontal ? adjustedModuleWidth : adjustedModuleLength) / effectiveStripSpacing
      );
  
      const totalCoveredSize = numStrips * effectiveStripSpacing;
      const remainingSpace = config.stripRotationHorizontal
        ? adjustedModuleWidth - totalCoveredSize + 0.005
        : adjustedModuleLength - totalCoveredSize;
  
      // Ensure stripsRef.current[moduleIndex] exists
      if (!stripsRef.current[moduleIndex]) {
        stripsRef.current[moduleIndex] = [];
      }
  
      for (let i = 0; i < numStrips; i++) {
        
        const xPos = config.stripRotationHorizontal
        ? position[0] - width / 2 + i * effectiveStripSpacing + frameThickness
        : position[0] -width /2 + frameThickness + stripSideMargin/2;
        
        const zPos = config.stripRotationHorizontal
        ? position[2] - length/2 + frameThickness + stripSideMargin/2
        // ? -position[1] + frameThickness + stripSideMargin/2 -1.08
        : i * effectiveStripSpacing -length/2 +stripWidth + frameThickness + stripSideMargin/2;
        
        const yPos = height - frameThickness/2;
  
        // Extrusion settings for the strip
        const extrudeSettings = {
          // length:config.stripRotationHorizontal ? 0 : adjustedModuleWidth,
          depth: config.stripRotationHorizontal ? adjustedModuleLength : adjustedModuleWidth,
          bevelEnabled: false
        };
  
        const stripGeometry = new THREE.ExtrudeGeometry(createStripProfile(), extrudeSettings);
        const stripMaterial = new THREE.MeshStandardMaterial({ color: config.color });
  
        const stripRotation = config.stripRotationHorizontal ? [0, 0, 0] : [0, Math.PI / 2, 0];
        const stripPosition = [xPos, yPos, zPos]
  
        strips.push(
          <mesh
            key={`strip-${moduleIndex}-${i}`}
            geometry={stripGeometry}
            material={stripMaterial}
            position={stripPosition}
            rotation={stripRotation}
            ref={(el) => {
              if (el) {
                stripsRef.current[moduleIndex][i] = el;
                if (!el.userData.originalPosition) {
                  el.userData.originalPosition = {
                    x: el.position.x,
                    y: el.position.y,
                    z: el.position.z,
                  };
                }
                el.userData.dimensions = {
                  width: adjustedModuleWidth,
                  depth: length,
                  height: height - stripThickness / 2,
                  stripWidth: stripWidth,
                };
              }
            }}
          >
            {config.roofLed && i % roofLedDistributionEvery === 0 && (
              <Box
                args={config.stripRotationHorizontal
                  ? [ledThickness, 0.01, roofLedLength]
                  : [ledThickness, 0.01, roofLedLength]
                } // LED size
                position={config.stripRotationHorizontal
                  ? [0.09, -0.045, adjustedModuleLength/2] // Position inside strip
                  : [0.09, -0.045, adjustedModuleWidth/2] // Position inside strip
                } // Position inside strip
              >
                <meshStandardMaterial 
                  color="white" 
                  emissive="white" 
                  toneMapped={false} 
                  emissiveIntensity={2}
                />
              </Box>
            )}
          </mesh>
        );
      }
  
      // **Fixed Strip for Remaining Space**
      // if (remainingSpace > 0) {
      //   const yPos = height - frameHeight + frameThickness;
      //   const xPos = config.stripRotationHorizontal
      //     ? position[0] + adjustedModuleWidth / 2 - remainingSpace / 2
      //     : position[0];
  
      //   const fixedExtrudeSettings = {
      //     depth: remainingSpace,
      //     bevelEnabled: false
      //   };
  
      //   const fixedStripGeometry = new THREE.ExtrudeGeometry(createStripProfile(), fixedExtrudeSettings);
      //   const fixedStripPosition = config.stripRotationHorizontal
      //     ? [xPos, yPos, 0]
      //     : [xPos, yPos, adjustedModuleLength / 2 - remainingSpace / 2];
  
      //   strips.push(
      //     <mesh key={`fixed-strip-${moduleIndex}`} geometry={fixedStripGeometry} position={fixedStripPosition}>
      //       <meshStandardMaterial color="gray" />
      //     </mesh>
      //   );
      // }

      if (remainingSpace > 0) {
        const yPos = height - frameThickness -0.01
        const xPos = config.stripRotationHorizontal
        ? position[0] + adjustedModuleWidth / 2 - remainingSpace / 2
        :position[0] ;
        
        const fixedStripArgs = config.stripRotationHorizontal
          ? [remainingSpace, stripThickness, adjustedModuleLength]
          : [adjustedModuleWidth, stripThickness, remainingSpace];
  
        const fixedStripPosition = config.stripRotationHorizontal
          ? [xPos, yPos, 0]
          : [xPos, yPos, adjustedModuleLength / 2 - remainingSpace / 2];
  
        strips.push(
          <Box key={`fixed-strip-${moduleIndex}`} args={fixedStripArgs} position={fixedStripPosition}>
            <meshStandardMaterial color={config.color} />
          </Box>
        );
      }
  
      return <React.Fragment key={`strips-module-${moduleIndex}`}>{strips}</React.Fragment>;
    });
  };
  
  const createStripProfile = () => {
    const svgPoints = [
      [14.6725713, 9.48196235],
      [1, 7.05485038],
      [1, 32.5352636],
      [15.1893339, 44.3067029],
      [128.592062, 44.3067029],
      [155.767339, 26.0330538],
      [155.767339, 6.99239688],
      [176.10213, 10.0613439],
      [176.163773, 3.99239688],
      [164.789055, 1],
      [73.7242782, 15.4438507],
      [73.7242782, 32.5352636],
      [6.44896264, 32.5352636],
      [6.81506731, 15.7501595]
    ];
  
    // **Original Dimensions from SVG**
    const originalWidth = 176.163773; // Max X value
    const originalHeight = 44.3067029; // Max Y value
  
    // **Target Dimensions**
    const targetWidth = 0.176; // 176mm → 0.176m
    const targetHeight = 0.044; // 44mm → 0.044m
  
    // **Scaling Factor**
    const scaleX = targetWidth / originalWidth;
    const scaleY = targetHeight / originalHeight;
  
    // **Flip and Scale the Shape**
    const shape = new THREE.Shape();
    shape.moveTo(svgPoints[0][0] * scaleX, -svgPoints[0][1] * scaleY); // Flip Y
    for (let i = 1; i < svgPoints.length; i++) {
      shape.lineTo(svgPoints[i][0] * scaleX, -svgPoints[i][1] * scaleY); // Flip Y
    }
    shape.closePath();
  
    return shape;
  };
  
  
  // **RENDER PERIMETER LEDS**
  const renderPerimeterLeds = () => {
    if (!config.perimeterLed) return null;
  
    return modules.map(({ position, width, isFirst, isLast }, i) => {
      const leds = [];
  
      const frontLedZ = adjustedLength / 2 - ledThickness - gutterSize/2;
      const backLedZ = -adjustedLength / 2 + ledThickness +gutterSize/2;
      const leftLedX = position[0] - width / 2 + ledThickness / 2 + gutterSize/2 + 0.01;
      const rightLedX = position[0] + width / 2 - ledThickness / 2 - gutterSize/2 - 0.01;
      
      const ledMaterial = (
        <meshStandardMaterial color="white" emissive="white" emissiveIntensity={2} toneMapped={false} />
      );
  
      // 🔹 Front LED (Always present)
      leds.push(
        <Box
          key={`front-led-${i}`}
          args={[width - 0.3, 0.01, ledThickness]}
          position={[position[0], perimeterLedHeight, frontLedZ]}
        >
          {ledMaterial}
        </Box>
      );
  
      // 🔹 Back LED (Always present)
      leds.push(
        <Box
          key={`back-led-${i}`}
          args={[width - 0.3, 0.01, ledThickness]}
          position={[position[0], perimeterLedHeight, backLedZ]}
        >
          {ledMaterial}
        </Box>
      );
  
      // 🔹 Left-side LED (Only for first module)
      if (isFirst) {
        leds.push(
          <Box
            key={`left-led-${i}`}
            args={[ledThickness, 0.01, adjustedLength - 0.3]}
            position={[leftLedX, perimeterLedHeight, 0]}
          >
            {ledMaterial}
          </Box>
        );
      }
  
      // 🔹 Right-side LED (Only for last module)
      if (isLast) {
        leds.push(
          <Box
            key={`right-led-${i}`}
            args={[ledThickness, 0.01, adjustedLength - 0.3]}
            position={[rightLedX, perimeterLedHeight, 0]}
          >
            {ledMaterial}
          </Box>
        );
      }
  
      return <React.Fragment key={`leds-module-${i}`}>{leds}</React.Fragment>;
    });
  };
  

  const renderDebugPlaceholders = () => (
      <>
        {/* 🔷 Total Pergola Dimensions */}
        <Box args={[config.width, height, length]} position={[0, height / 2, 0]}>
          <meshStandardMaterial color="blue" transparent opacity={0.2} wireframe />
        </Box>
    
        {/* 🔶 Module Debug Boxes */}
        {modules.map(({ position, width }, i) => (
          <Box key={`module-placeholder-${i}`} args={[width, height, length]} position={[position[0], position[1], position[2]]}>
            <meshStandardMaterial color="red" transparent opacity={0.2} wireframe />
          </Box>
        ))}
      </>
    );

    const renderRoofBoxes = () => {
      return modules.map(({ position, width, length }, i) => {    
        // **Roof dimensions should cover the strips inside**
        const adjustedModuleWidth = width - frameThickness * 2
    
        const adjustedModuleLength = length - frameThickness * 2;
    
        return (
          <Box
            key={`module-roof-placeholder-${i}`}
            args={[adjustedModuleWidth, stripThickness, adjustedModuleLength]} // Ensures strips fit inside
            position={[position[0], height - frameHeight + frameThickness/2, position[2]]} // Align with strips
          >
            <meshStandardMaterial color="purple" transparent opacity={0.2} wireframe />
          </Box>
        );
      });
    };
    
    
      

  return (
    <>
      {renderColumns()}
      {renderFrame()}
      {renderFrameContinuation()}
      {renderGutters()}
      {renderGlasses()}
      {renderStrips()}
      {renderPerimeterLeds()}
      {/* {renderDebugPlaceholders()} */}
      {/* {renderRoofBoxes()} */}

      {/* 🔹 Render Information Points */}
      {infosVisible&&infoPoints.map(
        (info, index) =>
          (
            <InfoPoint
              key={`info-${index}`}
              position={info.position}
              title={info.title}
              description={info.description}
              images={info.images}
              visible={info.visible}
              darkMode={darkMode}
            />
          )
      )}
    </>
  );
}

export default Pergola3D;
